import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import HeroCarousel, { type HeroSlide } from '@/components/feature/HeroCarousel';
import SectionTitle from '@/components/feature/SectionTitle';
import { categories } from '@/mocks/categories';
import { softwares } from '@/mocks/softwares';
import { formatDownloads } from '@/utils/format';

const featuredIds = ['gold-radar', 'gold-trend-master', 'gold-mobile-quote', 'gold-wealth-butler'];

const heroImages: Record<string, string> = {
  'gold-radar':
    'https://readdy.ai/api/search-image?query=Abstract%20elegant%20background%20of%20flowing%20golden%20light%20ribbons%20over%20deep%20navy%20blue%20gradient%2C%20luxurious%20financial%20technology%20theme%2C%20smooth%20silky%20texture%2C%20high%20contrast%20dark%20composition%20for%20text%20overlay%2C%20premium%20editorial%20visual&width=1600&height=600&seq=hero-gold-01&orientation=landscape',
  'gold-trend-master':
    'https://readdy.ai/api/search-image?query=Abstract%20dark%20navy%20background%20with%20glowing%20golden%20candlestick%20chart%20lines%20and%20data%20particles%2C%20financial%20analytics%20concept%2C%20deep%20blue%20tones%20with%20warm%20gold%20highlights%2C%20high%20contrast%20composition%20for%20text%20overlay&width=1600&height=600&seq=hero-gold-02&orientation=landscape',
  'gold-mobile-quote':
    'https://readdy.ai/api/search-image?query=Abstract%20minimal%20background%20of%20gold%20bullion%20bars%20silhouette%20with%20soft%20golden%20bokeh%20light%20on%20dark%20navy%20gradient%2C%20wealth%20and%20investment%20theme%2C%20elegant%20premium%20visual%20with%20strong%20contrast%20for%20headline%20text&width=1600&height=600&seq=hero-gold-03&orientation=landscape',
  'gold-wealth-butler':
    'https://readdy.ai/api/search-image?query=Abstract%20financial%20growth%20concept%20background%20with%20rising%20golden%20line%20graph%20and%20soft%20glow%20over%20deep%20navy%20blue%2C%20minimal%20elegant%20composition%2C%20warm%20gold%20and%20dark%20blue%20contrast%20for%20text%20overlay&width=1600&height=600&seq=hero-gold-04&orientation=landscape',
};

export default function Home() {
  const featured = featuredIds
    .map((id) => softwares.find((s) => s.id === id))
    .filter((s): s is (typeof softwares)[number] => Boolean(s));

  const slides: HeroSlide[] = featured.map((s) => ({
    id: s.id,
    name: s.name,
    summary: s.summary,
    categoryId: s.categoryId,
    categoryName: s.categoryName,
    heroImage: heroImages[s.id],
  }));

  const latest = [...softwares]
    .sort((a, b) => (a.publishDate < b.publishDate ? 1 : -1))
    .slice(0, 5);

  const hot = [...softwares].sort((a, b) => b.downloads - a.downloads).slice(0, 5);

  return (
    <SiteLayout>
      <HeroCarousel slides={slides} />

      <section className="mt-8">
        <SectionTitle
          title="软件分类快捷入口"
          subtitle="按功能快速找到适合你的黄金投资工具"
          icon="ri-apps-2-line"
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.id}`}
              className="group rounded-lg border border-background-200 bg-background-50 p-4 flex flex-col items-center text-center hover:border-primary-300 hover:bg-primary-50 transition-colors cursor-pointer"
            >
              <span className="w-12 h-12 rounded-lg bg-secondary-950 text-primary-400 flex items-center justify-center group-hover:bg-primary-500 group-hover:text-secondary-950 transition-colors">
                <i className={`${cat.icon} text-2xl`} />
              </span>
              <span className="mt-3 font-heading font-semibold text-sm text-foreground-950">
                {cat.name}
              </span>
              <span className="mt-1 text-xs text-foreground-500">{cat.tagline}</span>
              <span className="mt-2 text-[11px] text-primary-700">
                {softwares.filter((s) => s.categoryId === cat.id).length} 款软件
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="mt-10 grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <SectionTitle
            title="最新软件发布"
            subtitle="紧跟更新节奏，发现新上架工具"
            linkTo="/softwares"
            icon="ri-time-line"
          />
          <ul className="flex flex-col divide-y divide-background-200">
            {latest.map((s, i) => (
              <li key={s.id} className="py-3 first:pt-0 last:pb-0">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-md bg-accent-100 text-accent-700 text-xs font-semibold flex items-center justify-center shrink-0">
                    {i + 1}
                  </span>
                  <Link to={`/detail/${s.id}`} className="shrink-0 cursor-pointer">
                    <div className="w-12 h-12 rounded-md overflow-hidden bg-background-100">
                      <img
                        src={s.cover}
                        alt={`${s.name} 封面`}
                        title={`${s.name} 黄金投资软件`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/detail/${s.id}`}
                      className="font-medium text-sm text-foreground-950 cursor-pointer hover:text-primary-700 transition-colors truncate block"
                    >
                      {s.name}
                    </Link>
                    <p className="text-xs text-foreground-500 truncate">{s.summary}</p>
                  </div>
                  <Link
                    to={`/detail/${s.id}`}
                    className="shrink-0 h-8 px-3 rounded-md bg-primary-500 hover:bg-primary-600 text-secondary-950 text-xs font-medium whitespace-nowrap cursor-pointer transition-colors flex items-center gap-1"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-download-line text-sm" />
                    </span>
                    下载
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <SectionTitle
            title="热门软件下载"
            subtitle="下载量与口碑兼具的人气之选"
            linkTo="/softwares"
            icon="ri-fire-line"
          />
          <ul className="flex flex-col divide-y divide-background-200">
            {hot.map((s, i) => (
              <li key={s.id} className="py-3 first:pt-0 last:pb-0">
                <div className="flex items-center gap-3">
                  <span
                    className={`w-6 h-6 rounded-md text-xs font-semibold flex items-center justify-center shrink-0 ${
                      i < 3 ? 'bg-primary-500 text-secondary-950' : 'bg-background-200 text-foreground-600'
                    }`}
                  >
                    {i + 1}
                  </span>
                  <Link to={`/detail/${s.id}`} className="shrink-0 cursor-pointer">
                    <div className="w-12 h-12 rounded-md overflow-hidden bg-background-100">
                      <img
                        src={s.cover}
                        alt={`${s.name} 封面`}
                        title={`${s.name} 黄金投资软件`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/detail/${s.id}`}
                      className="font-medium text-sm text-foreground-950 cursor-pointer hover:text-primary-700 transition-colors truncate block"
                    >
                      {s.name}
                    </Link>
                    <p className="text-xs text-foreground-500 flex items-center gap-1">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-download-2-line text-sm" />
                      </span>
                      {formatDownloads(s.downloads)} 次下载
                    </p>
                  </div>
                  <Link
                    to={`/detail/${s.id}`}
                    className="shrink-0 h-8 px-3 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-xs font-medium whitespace-nowrap cursor-pointer transition-colors flex items-center gap-1"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-download-line text-sm" />
                    </span>
                    下载
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mt-10 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              关于黄金网站软件app免费
            </h2>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
              我们专注整理与推荐实用的黄金投资软件与应用，内容覆盖市场行情分析、价格走势监控、黄金定投与理财工具等多个方向。
              无论你是刚开始关注金价走势的新手，还是长期跟踪黄金市场的理财用户，都可以在这里按分类快速查找、了解并下载合适的工具，
              用更高效的方式掌握黄金资讯与投资节奏。
            </p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: 'ri-shield-check-line', title: '信息条理清晰', desc: '分类与筛选完善，快速定位所需工具' },
                { icon: 'ri-time-line', title: '更新及时', desc: '持续收录新版本与热门行情应用' },
                { icon: 'ri-book-2-line', title: '说明详实', desc: '功能介绍与规格一览，下载前更放心' },
              ].map((item) => (
                <div key={item.title} className="rounded-lg bg-background-50 border border-background-200 p-4">
                  <span className="w-9 h-9 rounded-md bg-primary-100 text-primary-800 flex items-center justify-center">
                    <i className={`${item.icon} text-lg`} />
                  </span>
                  <h3 className="mt-3 font-semibold text-sm text-foreground-950">{item.title}</h3>
                  <p className="mt-1 text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg bg-secondary-950 p-6 text-background-50">
            <h3 className="font-heading text-lg font-bold text-primary-400">服务承诺</h3>
            <ul className="mt-4 flex flex-col gap-3 text-sm text-background-200">
              {[
                '所有收录软件均标注分类、版本与更新时间',
                '提供功能简介与规格说明，下载前先了解',
                '支持按功能、时间与热度多维筛选',
                '本页为演示站点，下载链接为示范地址',
              ].map((text) => (
                <li key={text} className="flex items-start gap-2">
                  <span className="w-4 h-4 mt-0.5 flex items-center justify-center text-primary-400 shrink-0">
                    <i className="ri-checkbox-circle-line text-base" />
                  </span>
                  <span className="leading-relaxed">{text}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/about"
              className="mt-6 h-10 rounded-md bg-primary-500 hover:bg-primary-600 text-secondary-950 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors flex items-center justify-center gap-1"
            >
              了解更多
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-base" />
              </span>
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}